import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { BornSoloCrumbs } from 'subscription/constants/breadcrumb';
import { initialValues } from 'subscription/constants/subscription-birth';
import { toast } from 'react-toastify';
import { ResponseAnprSaveData } from 'shell/models/person';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import validationSchemaBornSolo from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionButtons from 'subscription/components/shared/buttons';
import { IDFlags } from 'shell/components/shared/flag-validation-fiscal-code/flag';
import SubscriptionTranscriptionBirthAct from 'subscription/components/transcription-birth-act';
import { ActType } from 'shell/constants/act-type';
import { emptyPaper } from 'shell/components/shared/act/constants';
import { SubscriptionTranscriptionBirthActFormVM } from 'subscription/view-models/subscription-transcription-birth-act';
import { transformerRequestSubscriptionTranscriptionBirthActAireSoloData } from 'subscription/utils/transcription-birth-act-lonely.request-transformer';

/**
 * BornSolo
 *
 * Component that renders the form for transcription of a birth act for a person who is not a resident
 *
 * @returns {React.ReactElement} The rendered component
 */
const BornSolo = (): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [responseRegister, register] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-nascita/aire`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {
        criteriRicerca: {
          codiceFiscale: fiscalCode || undefined,
          idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          soggettoAIRE: 'S',
        },
        datiRichiesta: {
          comuneResidenza:
            filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
            filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
            filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
            filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
              ? {
                  siglaProvinciaIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.province ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.province ||
                    undefined,
                  codiceIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                    undefined,
                  nomeComune:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                      ?.description ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                      ?.description ||
                    undefined,
                }
              : undefined,
          dataRiferimentoRichiesta:
            filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
            undefined,
          ricercaNazionale: nationalSearch ? '1' : '0',
        },
      },
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegister?.data as SaveDataAnpr;

  const handleSubmit = useEventCallback((values: SubscriptionTranscriptionBirthActFormVM) => {
    const data = transformerRequestSubscriptionTranscriptionBirthActAireSoloData(values);
    (
      register({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;
      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate('/iscrizioni');
    });
  });

  const handleExit = useCallback(() => {
    navigate('/iscrizioni');
  }, [navigate]);

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  return (
    <Fragment>
      <Breadcrumbs items={BornSoloCrumbs} />
      <div>
        <PmiTitle title="Iscrizione AIRE atto di nascita - Solo" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <Formik
        initialValues={
          {
            ...initialValues,
            addressForeign: {
              address: '',
            },
            citizenship: {
              ...initialValues.citizenship,
              state: { id: '100', value: 'ITALIA' },
            },
            familyCoexistence: undefined,
            editDataControl: {
              ...initialValues.editDataControl,
              flag: IDFlags.Attribuzione,
              subscribeTo: '5', // "Altra famiglia" fisso
              dateValidity: null,
            },
            generality: {
              ...initialValues.generality,
              isForeign: true,
              aire: 'S',
            },
            birthdayDocument: {
              actType: ActType.AttoCartaceo,
              paper: {
                ...emptyPaper,
                transcribed: true, // fisso per trascrizione nascita solo
              },
            },
          } as SubscriptionTranscriptionBirthActFormVM
        }
        onSubmit={handleSubmit}
        validationSchema={validationSchemaBornSolo}
      >
        <Form>
          <Grid container spacing={2}>
            <SubscriptionButtons handleExit={handleExit} />
            <Grid item xs={12} md={10}>
              <SubscriptionTranscriptionBirthAct
                isBornSolo
                isAire
                withoutFamily
                hasResearchSipo
                urlEditSipo="/iscrizioni/aire-nascita-solo/persona-sipo"
                urlBackendSipo="/non-residenti/ricerca-intestatario-nascita-aire"
              />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Fragment>
  );
};

BornSolo.displayName = 'Subscription Born solo page component';

export default BornSolo;
